/**
 * Tune4Tune Privacy Policy — GDPR-shaped and written in plain English.
 *
 * NOT LEGAL ADVICE. Reviewed by a US/EU privacy lawyer before launch.
 * Company details (Haul And Pull LLC, Florida) are sourced from the operator
 * so they're consistent across Terms + Subprocessors + email footers.
 */
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const CONTACT_EMAIL = 'hallandpull@gmail.com';
const LEGAL_ENTITY = 'Haul And Pull LLC';
const JURISDICTION = 'Florida, USA';
const LAST_UPDATED = 'February 2026';

function Section({ id, title, children }) {
  return (
    <section id={id} className="space-y-3 text-[#D4D4D8] text-sm leading-relaxed">
      <h2 className="text-xl font-heading font-bold text-white pt-6 border-t border-[#1a1a1a] first:border-none first:pt-0">
        {title}
      </h2>
      {children}
    </section>
  );
}

function List({ children }) {
  return <ul className="list-disc pl-6 space-y-1.5">{children}</ul>;
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-24 space-y-10" data-testid="privacy-page">
        <header className="space-y-2">
          <p className="text-lime text-xs uppercase tracking-widest font-semibold">Legal</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Privacy Policy</h1>
          <p className="text-[#A1A1AA] text-sm">Last updated: {LAST_UPDATED}</p>
        </header>

        <Section id="who-we-are" title="1. Who we are">
          <p>
            Tune4Tune ("Tune4Tune", "we", "us") is a marketplace that connects
            Caribbean Dancehall & Reggae artists with fans who want custom
            dubplates and voiceovers. Tune4Tune is operated by{' '}
            <strong className="text-white">{LEGAL_ENTITY}</strong>, a limited
            liability company registered in <strong className="text-white">{JURISDICTION}</strong>.
          </p>
          <p>
            {LEGAL_ENTITY} is the <strong className="text-white">data controller</strong>{' '}
            for the personal data described in this policy. Contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-lime hover:underline">{CONTACT_EMAIL}</a>{' '}
            for any privacy question.
          </p>
        </Section>

        <Section id="what-we-collect" title="2. What we collect">
          <p><strong className="text-white">Directly from you</strong> when you register, browse, order, or upload:</p>
          <List>
            <li>Account details: name, email, password (hashed with bcrypt), avatar, role (fan / artist / agent).</li>
            <li>Artist &amp; agent profiles: stage name, bio, genres, sample audio, cover art, pricing.</li>
            <li>Order details: song title, dub message / brief, delivery preferences, price paid, artist earnings.</li>
            <li>Payment metadata (never the raw card): last-4 digits, brand, Stripe customer + payment intent IDs. Card numbers are handled entirely by Stripe.</li>
            <li>Uploads: recordings, mixdowns, instrumentals you create in the Studio.</li>
            <li>Communications: order messages, dispute submissions, support emails.</li>
          </List>
          <p><strong className="text-white">Automatically</strong> when you use the site:</p>
          <List>
            <li>IP address, user agent, referrer (for security, fraud detection, and login-attempt monitoring).</li>
            <li>Session and CSRF cookies (strictly necessary — see cookies section below).</li>
            <li>Basic error/crash telemetry so we can fix bugs.</li>
          </List>
        </Section>

        <Section id="why-we-collect" title="3. Why we collect it (legal basis)">
          <List>
            <li><strong className="text-white">Performance of a contract</strong> — creating your account, taking orders, delivering dubplates, paying artists.</li>
            <li><strong className="text-white">Legitimate interest</strong> — protecting the platform from fraud and abuse, improving the product, sending transactional emails you'd expect from a service you signed up for.</li>
            <li><strong className="text-white">Legal obligation</strong> — tax records, complying with subpoenas, responding to regulator requests.</li>
            <li><strong className="text-white">Consent</strong> — where required, e.g. marketing emails outside your existing contract. You can withdraw consent at any time.</li>
          </List>
        </Section>

        <Section id="cookies" title="4. Cookies">
          <p>
            We use only <strong className="text-white">strictly necessary cookies</strong>:
          </p>
          <List>
            <li><code className="text-lime">auth_token</code> — httpOnly session cookie so you stay logged in.</li>
            <li><code className="text-lime">t4t_access</code> — session flag for the pre-launch access gate.</li>
            <li>Stripe cookies — set by Stripe on their own domain during checkout to prevent card fraud.</li>
          </List>
          <p>
            We do <strong className="text-white">not</strong> run Google Analytics, Meta Pixel, or any advertising trackers. Because our cookies are strictly necessary for the service to work, no consent banner is required under the ePrivacy Directive and GDPR.
          </p>
        </Section>

        <Section id="sharing" title="5. Who we share your data with">
          <p>
            We do not sell your personal data. We share it only with the vendors we need to run the service ("subprocessors"). A live list, with links to each processor's privacy policy and DPA, lives at{' '}
            <Link to="/legal/subprocessors" className="text-lime hover:underline">/legal/subprocessors</Link>. Today that list includes:
          </p>
          <List>
            <li><strong className="text-white">Stripe, Inc.</strong> — card payments, artist payouts. Certified under the EU–US Data Privacy Framework.</li>
            <li><strong className="text-white">Resend</strong> — transactional email delivery (verification, receipts, order updates).</li>
            <li><strong className="text-white">MongoDB Atlas</strong> — encrypted database hosting.</li>
            <li><strong className="text-white">Emergent</strong> — application hosting, object storage for uploads, LLM-assisted moderation.</li>
          </List>
          <p>
            We share the minimum necessary and only for the specific purpose above. We do not send your data to any other party unless you ask us to (for example, when you deliver a dubplate to a buyer).
          </p>
        </Section>

        <Section id="international-transfers" title="6. International transfers">
          <p>
            Tune4Tune is operated from the United States. If you access the service from the EU / UK / EEA, your personal data will be transferred outside your country. We rely on the <strong className="text-white">EU–US Data Privacy Framework</strong> (via our subprocessors that self-certify) and, where DPF is not available, on <strong className="text-white">Standard Contractual Clauses</strong> plus additional technical safeguards (encryption at rest and in transit).
          </p>
        </Section>

        <Section id="retention" title="7. How long we keep your data">
          <List>
            <li><strong className="text-white">Account &amp; profile</strong> — for the life of the account. Deleted 30 days after you request account deletion (see Section 9).</li>
            <li><strong className="text-white">Orders, invoices, tax records</strong> — 7 years after the order (US tax retention requirement). This survives account deletion so we can meet legal obligations.</li>
            <li><strong className="text-white">Uploads &amp; delivered files</strong> — until you delete them, or 30 days after account deletion.</li>
            <li><strong className="text-white">Messages</strong> — 2 years, or until account deletion.</li>
            <li><strong className="text-white">Login attempts &amp; security logs</strong> — 12 months.</li>
            <li><strong className="text-white">Privacy request logs</strong> — 3 years (so we can prove your requests were honoured).</li>
          </List>
        </Section>

        <Section id="security" title="8. How we protect it">
          <List>
            <li>TLS everywhere; strict HSTS + Content Security Policy headers.</li>
            <li>Passwords stored as bcrypt hashes only.</li>
            <li>Two-factor authentication (TOTP) on admin accounts.</li>
            <li>Encryption at rest for the database and object storage.</li>
            <li>Role-based access control on every backend endpoint.</li>
            <li>Rate limiting and brute-force protection on login and reset flows.</li>
            <li>All admin actions written to an audit log.</li>
          </List>
        </Section>

        <Section id="your-rights" title="9. Your rights">
          <p>Wherever you're based, you can:</p>
          <List>
            <li><strong className="text-white">Access</strong> a machine-readable copy of everything we hold about you — <Link to="/settings?tab=privacy" className="text-lime hover:underline">Settings → Privacy → Download my data</Link>.</li>
            <li><strong className="text-white">Rectify</strong> anything wrong — most fields are editable from your profile; email us for the rest.</li>
            <li><strong className="text-white">Delete your account</strong> — <Link to="/settings?tab=privacy" className="text-lime hover:underline">Settings → Privacy → Delete my account</Link>. You have a 30-day grace period to change your mind; after that the account is permanently purged (except records we are legally required to keep, e.g. tax invoices).</li>
            <li><strong className="text-white">Object</strong> to processing based on legitimate interest.</li>
            <li><strong className="text-white">Restrict</strong> processing while a dispute is resolved.</li>
            <li><strong className="text-white">Port</strong> your data — the export in Section 9(1) is a portable JSON.</li>
            <li><strong className="text-white">Withdraw consent</strong> where processing is based on consent (this doesn't affect processing done before the withdrawal).</li>
            <li><strong className="text-white">Complain</strong> to your local supervisory authority. In the EU, that's usually your national data-protection authority; in the UK it's the ICO.</li>
          </List>
          <p>
            To exercise any right, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-lime hover:underline">{CONTACT_EMAIL}</a>. We respond within 30 days.
          </p>
        </Section>

        <Section id="children" title="10. Children">
          <p>
            Tune4Tune is not directed at children under 13 (or under 16 in the EU / UK). We do not knowingly collect personal data from anyone in that age range. If you believe a child has provided us data, email us and we'll delete it.
          </p>
        </Section>

        <Section id="changes" title="11. Changes to this policy">
          <p>
            If we make material changes we'll notify you via email or an in-app banner at least 14 days before they take effect. The "Last updated" date at the top tracks the most recent revision.
          </p>
        </Section>

        <Section id="contact" title="12. Contact">
          <p>
            {LEGAL_ENTITY}<br />
            {JURISDICTION}<br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-lime hover:underline">{CONTACT_EMAIL}</a>
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
